import React, { useRef } from 'react';
import { mergeProps, useButton, useHover } from 'react-aria';
import { useMergedRefs } from 'hooks';
import { UnstyledButtonProps } from './types';

function ForwardedUnstyledButton<
  T extends Element = HTMLButtonElement,
  As extends React.ElementType = 'button',
>(
  {
    as,
    children,
    className,
    excludeFromTabOrder,
    isDisabled,
    onHoverChange,
    onHoverEnd,
    onHoverStart,
    onPress,
    onPressChange,
    onPressEnd,
    onPressStart,
    onPressUp,
    ...props
  }: UnstyledButtonProps<As>,
  forwardedRef: React.ForwardedRef<T>,
) {
  const Component: React.ElementType = as ?? 'button';

  const ariaRef = useRef<T>(null);
  const ref = useMergedRefs<T>([ariaRef, forwardedRef]);

  const { buttonProps, isPressed } = useButton(
    {
      ...props,
      excludeFromTabOrder,
      isDisabled,
      onPress,
      onPressChange,
      onPressEnd,
      onPressStart,
      onPressUp,
      elementType: as,
    },
    ariaRef,
  );

  const { hoverProps, isHovered } = useHover({
    isDisabled,
    onHoverChange,
    onHoverEnd,
    onHoverStart,
  });

  const elementProps = mergeProps(buttonProps, hoverProps, props);

  return (
    <Component
      data-hovered={isHovered}
      data-pressed={isPressed}
      {...{ className, ref }}
      {...elementProps}
    >
      {children}
    </Component>
  );
}

/**
 * An button with no styles used to build other clickable components.
 * 
 * By default, this components renders as a native HTML `<button>` element, however
 * the `as` prop can be used to render the button as any other type of element.  This is
 * useful when a design calls for nested clickable elements (nested buttons is invalid
 * HTML) or when using libraries like framer-motion which would require the button to
 * be rendered as `motion.button`.

 */
export const UnstyledButton = React.forwardRef(ForwardedUnstyledButton) as <
  T extends Element = HTMLButtonElement,
  As extends React.ElementType = 'button',
>(
  props: UnstyledButtonProps<As> & { ref?: React.Ref<T> },
) => React.ReactElement | null;

export default UnstyledButton;
